/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { safeLocalStorage } from "@/helper/safeLocalStorage";

/** localStorage key for legacy all-or-nothing consent (kept for backward compat). */
export const CONSENT_KEY = "ds_cookie_consent";

/** localStorage key for granular per-consent record. */
export const PREFS_KEY = "ds_cookie_prefs";

/**
 * The active Privacy Policy version string — single source of truth.
 *
 * Read from the VITE_POLICY_VERSION environment variable, which Vite also
 * substitutes into index.html via the %VITE_POLICY_VERSION% placeholder at
 * build time. Both this import and the inline pre-loader script therefore
 * always read the same value and can never disagree.
 *
 * To re-prompt every visitor for cookie consent on the next visit (required
 * under GDPR Article 7 when the Privacy Policy materially changes), set
 * VITE_POLICY_VERSION to a new string (e.g. "v2.0") in the deployment
 * environment and rebuild. The fallback "v1.0" is used only in environments
 * where the variable is not set (e.g. local dev without a .env file).
 */
export const CURRENT_POLICY_VERSION =
  /* c8 ignore next -- env var is inlined by Vite at build time; unreachable in test */
  import.meta.env.VITE_POLICY_VERSION ?? "v1.0";

/** Maximum age of a stored consent record before it expires (12 months). */
const CONSENT_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * Full consent record stored in localStorage.
 * Replaces the bare { analytics: boolean } shape from prior sessions.
 *
 * Fields:
 *   analytics     — whether the user consented to Google Analytics
 *   timestamp     — ISO 8601 UTC string: when consent was given
 *   policyVersion — must match CURRENT_POLICY_VERSION for record to be valid
 *   source        — how consent was recorded (for audit trail)
 */
export interface ConsentRecord {
  analytics: boolean;
  timestamp: string;
  policyVersion: string;
  source: "banner_accept" | "banner_decline" | "settings_update";
}

/**
 * Validate a stored ConsentRecord.
 * Returns false when:
 *   - required fields are missing (legacy record)
 *   - the record is older than CONSENT_EXPIRY_MS (12 months)
 *   - the policy version does not match CURRENT_POLICY_VERSION
 */
function isConsentValid(r: ConsentRecord | null): boolean {
  if (!r?.timestamp || !r?.policyVersion) return false;
  if (Date.now() - new Date(r.timestamp).getTime() > CONSENT_EXPIRY_MS)
    return false;
  return r.policyVersion === CURRENT_POLICY_VERSION;
}

/**
 * Read stored consent, returning null when:
 *   - no record exists (banner should show)
 *   - record is expired or under an old policy version (banner re-shows)
 *   - legacy bare-boolean record detected (force re-consent)
 *
 * Side-effects: removes stale/expired records from localStorage.
 */
export function readCookiePrefs(): ConsentRecord | null {
  const raw = safeLocalStorage.getItem(PREFS_KEY);
  if (raw) {
    try {
      const rec = JSON.parse(raw) as ConsentRecord;
      if (isConsentValid(rec)) return rec;
      // Expired or stale policy — clear and re-show banner.
      safeLocalStorage.removeItem(PREFS_KEY);
      safeLocalStorage.removeItem(CONSENT_KEY);
      return null;
    } catch {
      // Malformed JSON — fall through.
    }
  }

  // Legacy bare-boolean record: force re-consent so a proper ConsentRecord
  // with timestamp and policy version is written on next decision.
  const legacy = safeLocalStorage.getItem(CONSENT_KEY);
  if (legacy) safeLocalStorage.removeItem(CONSENT_KEY);
  return null;
}
